import type { LinkField } from '@sitecore-content-sdk/nextjs';
import type { Data } from '@generated/Foundation.GMI.model';
import type { SiteSettings } from 'lib/page-props-factory/plugins/site-settings';

// Default fallback URLs for user profile pages
export const DEFAULT_LOGIN_URL = '/user-profile/login';
export const DEFAULT_SIGNUP_URL = '/user-profile/register';
export const DEFAULT_MY_PROFILE_URL = '/user-profile/my-account';
export const DEFAULT_FAVORITES_URL = '/user-profile/favorites';

export interface UserProfileUrls {
  loginUrl: string;
  signupUrl: string;
  myProfileUrl: string;
  manageAccountUrl: string;
  favoritesUrl: string;
}

/**
 * Extracts user profile URLs (login, signup) from site settings
 * Falls back to provided default URLs if not configured in site settings
 * @param siteSettings - Site settings object containing login/registration settings
 * @returns Object containing user profile URLs
 */
export function extractUserProfileUrls(
  siteSettings: SiteSettings | null | undefined
): UserProfileUrls {
  const loginRegistrationSettings =
    siteSettings?.loginRegistrationSettings as Data.Settings.LoginAndRegistrationSettings;

  // Extract login URL from site settings
  const loginPageField = loginRegistrationSettings?.fields?.loginPage as LinkField | undefined;
  const loginUrl = (loginPageField as LinkField)?.value?.href || DEFAULT_LOGIN_URL;

  // Extract signup/registration URL from site settings
  const registrationPageField = loginRegistrationSettings?.fields?.registrationPage as
    | LinkField
    | undefined;
  const signupUrl = (registrationPageField as LinkField)?.value?.href || DEFAULT_SIGNUP_URL;

  // Extract my-profile URL from site settings
  const myProfilePageField = loginRegistrationSettings?.fields?.myProfilePage as
    | LinkField
    | undefined;
  const myProfileUrl = (myProfilePageField as LinkField)?.value?.href || DEFAULT_MY_PROFILE_URL;

  // Extract favorites URL from site settings
  const favoritesPageField = (loginRegistrationSettings?.fields as Record<string, unknown>)
    ?.favoritesPage as LinkField | undefined;
  const favoritesUrl = (favoritesPageField as LinkField)?.value?.href || DEFAULT_FAVORITES_URL;

  return {
    loginUrl,
    signupUrl,
    myProfileUrl,
    manageAccountUrl: myProfileUrl,
    favoritesUrl,
  };
}
